import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2 } from "lucide-react";

const ROLE_COLOR = {
  owner: "text-primary", manager: "text-secondary", captain: "text-amber-400", client: "text-emerald-400",
};

export default function RemarksSection({ jobId, remarks = [], currentUserId }) {
  const qc = useQueryClient();
  const [message, setMessage] = useState("");

  const mutation = useMutation({
    mutationFn: async () => (await api.post(`/jobs/${jobId}/remarks`, { message })).data,
    onSuccess: () => {
      setMessage("");
      qc.invalidateQueries({ queryKey: ["job", jobId] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div data-testid="remarks-section">
      <h2 className="font-heading text-xl font-bold mb-3">Remarks &amp; Discussion</h2>
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="space-y-4 max-h-80 overflow-y-auto scrollbar-thin mb-4" data-testid="remarks-list">
          {remarks.length === 0 && <p className="text-sm text-muted-foreground">No remarks yet. Start the conversation.</p>}
          {remarks.map((r) => (
            <div key={r.id} className={`flex ${r.author_id === currentUserId ? "justify-end" : "justify-start"}`} data-testid={`remark-${r.id}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 ${r.author_id === currentUserId ? "bg-primary/10 border border-primary/30" : "bg-accent"}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-semibold">{r.author_name}</span>
                  <span className={`text-[10px] font-mono-tag uppercase ${ROLE_COLOR[r.author_role]}`}>{ROLE_LABELS[r.author_role]}</span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{r.message}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{fmtDate(r.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (message.trim()) mutation.mutate(); }} className="flex gap-2">
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={1} placeholder="Write a remark…"
            className="min-h-[42px] resize-none" data-testid="remark-input" />
          <Button type="submit" disabled={mutation.isPending || !message.trim()} data-testid="send-remark-button">
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      </div>
    </div>
  );
}
